import { plainToInstance } from 'class-transformer';
import { BusinessEntity } from '@/models';
import { mappingTypes } from '@/enums/generic';

/**
 * Flattens an already-transformed BusinessEntity into a Map of all entities.
 * Use this when you already have a BusinessEntity class instance.
 *
 * @param businessEntity - An already-transformed BusinessEntity instance
 * @returns Map<string, Entity> - Flat map of all entities by ID
 */
const flattenToMap = (businessEntity: BusinessEntity): Map<string, any> => {
  const entityMappingTypes = Object.values(mappingTypes);

  const entityMap = new Map();
  entityMap.set(businessEntity.id, businessEntity);

  entityMappingTypes.forEach((key) => {
    const keyBusinessEntity = key as keyof BusinessEntity;
    const businessEntityObjects = businessEntity[keyBusinessEntity] as Array<
      Record<any, any>
    >;

    if (Array.isArray(businessEntityObjects)) {
      businessEntityObjects.forEach((entity) => {
        entityMap.set(entity.id, entity);
      });
    }
  });

  return entityMap;
};

/**
 * Transforms raw JSON into a BusinessEntity and flattens to a Map.
 * Use this when you have raw JSON (from API or localStorage).
 *
 * @param flatBusinessEntity - Raw JSON object (not a class instance)
 * @returns Map<string, Entity> - Flat map of all entities by ID
 */
const plainToClassEntity = (flatBusinessEntity: any) => {
  const businessEntity: BusinessEntity = plainToInstance(
    BusinessEntity,
    flatBusinessEntity
  );

  return flattenToMap(businessEntity);
};

export default {
  plainToClassEntity,
  flattenToMap,
};
